import { useEffect, useCallback, useRef } from 'react'
import { Icon } from '@iconify/react'
import { useTabsContext } from './TabsContext'
import type { TabProps } from './types'
import styles from './Tabs.module.css'

/**
 * Botão individual de aba.
 * Registra-se automaticamente no contexto quando montado.
 */
export function Tab({ children, value, icon, disabled = false, badge, className }: TabProps) {
  const { activeTab, setActiveTab, tabsId, registerTab, unregisterTab } = useTabsContext()
  const buttonRef = useRef<HTMLButtonElement>(null)
  
  // Registra a aba quando montada
  useEffect(() => {
    registerTab({
      value,
      label: typeof children === 'string' ? children : value,
      icon,
      badge,
      disabled,
    })
    
    return () => {
      unregisterTab(value)
    }
  }, [value, children, icon, badge, disabled, registerTab, unregisterTab])
  
  const isActive = activeTab === value
  
  const handleClick = useCallback(() => {
    if (!disabled) {
      setActiveTab(value)
    }
  }, [disabled, setActiveTab, value])
  
  // Navegação por teclado
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return
      
      const tabs = buttonRef.current?.closest('[role="tablist"]')?.querySelectorAll('[role="tab"]')
      if (!tabs) return
      
      const tabsArray = Array.from(tabs) as HTMLButtonElement[]
      const currentIndex = tabsArray.findIndex((tab) => tab === buttonRef.current)
      
      let nextIndex: number | null = null
      
      switch (e.key) {
        case 'ArrowLeft':
          nextIndex = currentIndex > 0 ? currentIndex - 1 : tabsArray.length - 1
          break
        case 'ArrowRight':
          nextIndex = currentIndex < tabsArray.length - 1 ? currentIndex + 1 : 0
          break
        case 'Home':
          nextIndex = 0
          break
        case 'End':
          nextIndex = tabsArray.length - 1
          break
        default:
          return
      }
      
      e.preventDefault()
      const nextTab = tabsArray[nextIndex]
      nextTab?.focus()
      nextTab?.click()
    },
    [disabled]
  )
  
  const tabId = `${tabsId}-tab-${value}`
  const panelId = `${tabsId}-panel-${value}`
  
  return (
    <button
      ref={buttonRef}
      id={tabId}
      role="tab"
      aria-selected={isActive}
      aria-controls={panelId}
      aria-disabled={disabled}
      tabIndex={isActive ? 0 : -1}
      data-tab-value={value}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={[
        styles.tab,
        isActive && styles.tabActive,
        disabled && styles.tabDisabled,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {icon && <Icon icon={icon} className={styles.tabIcon} />}
      <span className={styles.tabLabel}>{children}</span>
      {badge !== undefined && (
        <span className={styles.tabBadge}>{badge}</span>
      )}
    </button>
  )
}
